import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Box, Button, CircularProgress, Stack, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';

export const MessageContainer = props => (
  <Box
    {...props}
    sx={[
      {
        margin: 'auto',
        textAlign: 'center',
        flexShrink: 0,
        px: 4,
        color: 'text.secondary',
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

MessageContainer.propTypes = {
  sx: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

export const EmptyMessageContainer = ({icon, children, ...props}) => (
  <MessageContainer {...props}>
    {icon && (
      <Box
        sx={{
          fontSize: 48,
        }}
      >
        {icon}
      </Box>
    )}
    {children}
  </MessageContainer>
);

EmptyMessageContainer.propTypes = {
  icon: PropTypes.element,
  children: PropTypes.node,
};

export const LoadingMessage = ({
  suffix = '',
  progressSize,
  titleTypographyProps = {},
  message = '',
  subheaderTypographyProps = {},
}) => {
  const {t} = useTranslation();

  return (
    <MessageContainer data-qe="loading-message">
      <Box
        sx={{
          marginBottom: 2,
          width: '100%',
        }}
      >
        <CircularProgress color="primary" size={progressSize} />
      </Box>
      <Typography
        variant="h5"
        color="inherit"
        gutterBottom
        {...titleTypographyProps}
      >
        {message ? message : t('messageContainer.titleLoading', {item: suffix})}
      </Typography>
      <Typography variant="h6" color="inherit" {...subheaderTypographyProps}>
        {t('messageContainer.messageLoading')}
      </Typography>
    </MessageContainer>
  );
};

LoadingMessage.propTypes = {
  message: PropTypes.string,
  suffix: PropTypes.string,
  titleTypographyProps: PropTypes.object,
  subheaderTypographyProps: PropTypes.object,
  progressSize: PropTypes.number,
};

export const ErrorMessage = ({
  error,
  message,
  icon = true,
  children,
  componentName = 'page',
  ...props
}) => {
  const {t} = useTranslation();

  return (
    <EmptyMessageContainer
      icon={
        icon && <ErrorOutlineOutlinedIcon fontSize="inherit" color="error" />
      }
      data-qe="error-message"
    >
      {children ? (
        children
      ) : (
        <Fragment>
          <Typography variant="h6" color="inherit" gutterBottom>
            {t('error.title')}
          </Typography>
          {message && (
            <Typography variant="h6" color="inherit" gutterBottom>
              {message}
            </Typography>
          )}
          {error && (
            <Typography variant="overline" color="inherit" gutterBottom>
              {error}
            </Typography>
          )}
          <Box
            sx={{
              mt: 4,
            }}
          >
            <Typography variant="h6" color="inherit" gutterBottom>
              {t('messageContainer.promptReload', {
                componentName,
              })}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 2,
            }}
          >
            <Button
              color="primary"
              variant="outlined"
              onClick={() => props.onRetry()}
              startIcon={<RefreshIcon />}
            >
              {t('messageContainer.buttonReload')}
            </Button>
            <Button
              color="primary"
              variant="contained"
              href="https://support.planet.com/hc/en-us/requests/new"
              target="_blank"
              rel="noreferrer noopener"
              endIcon={<OpenInNewIcon fontSize="small" />}
            >
              {t('messageContainer.buttonReport')}
            </Button>
          </Stack>
        </Fragment>
      )}
    </EmptyMessageContainer>
  );
};

ErrorMessage.propTypes = {
  error: PropTypes.string,
  message: PropTypes.string,
  children: PropTypes.node,
  icon: PropTypes.bool,
  componentName: PropTypes.string,
  onRetry: PropTypes.func,
};

export default MessageContainer;
